import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  XMarkIcon,
  CalendarIcon,
  UserIcon,
  TagIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  ChatBubbleLeftRightIcon,
  DocumentIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

const RequestDetails = ({ request, onClose, onUpdate, user }) => {
  const [loading, setLoading] = useState(false);
  const [showWorkflow, setShowWorkflow] = useState(false);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      submitted: 'bg-blue-100 text-blue-800',
      under_review: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      in_progress: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || colors.draft;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return colors[priority] || colors.medium;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      it: '💻',
      hr: '👥',
      admin: '📋',
      facilities: '🏢',
      other: '📝'
    };
    return icons[category] || icons.other;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      it: 'IT Support',
      hr: 'HR Services',
      admin: 'Administrative',
      facilities: 'Facilities',
      other: 'Other'
    };
    return labels[category] || category;
  };

  const handleWorkflowAction = async (action, comments = '') => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/request-system/${request.id}/workflow`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action,
          comments
        })
      });

      if (response.ok) {
        toast.success(`Request ${action}d successfully`);
        onUpdate();
      } else {
        const error = await response.json();
        toast.error(error.error || `Failed to ${action} request`);
      }
    } catch (error) {
      console.error('Error updating workflow:', error);
      toast.error(`Failed to ${action} request`);
    } finally {
      setLoading(false);
    }
  };

  const canTakeAction = () => {
    const userRole = user?.Role?.name || user?.role?.name;
    return ['admin', 'hr', 'super_admin'].includes(userRole) || 
           request.assignedTo === user?.id;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <span className="text-lg">{getCategoryIcon(request.category)}</span>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">{request.title}</h3>
                <p className="text-sm text-gray-500">Request #{request.id}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6">
            {/* Request Info */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Request Information</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Category:</span>
                      <span className="text-sm text-gray-900">{getCategoryLabel(request.category)}</span>
                    </div>
                    {request.subcategory && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Subcategory:</span>
                        <span className="text-sm text-gray-900">{request.subcategory}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Status:</span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                        {request.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Priority:</span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(request.priority)}`}>
                        {request.priority}
                      </span>
                    </div>
                    {request.isUrgent && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Urgent:</span>
                        <span className="text-sm text-red-600 font-medium">Yes</span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Description</h4>
                  <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                    {request.description}
                  </p>
                </div>

                {/* Cost Information */}
                {(request.estimatedCost || request.actualCost) && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Cost Information</h4>
                    <div className="space-y-2">
                      {request.estimatedCost && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Estimated Cost:</span>
                          <span className="text-sm text-gray-900">${request.estimatedCost}</span>
                        </div>
                      )}
                      {request.actualCost && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Actual Cost:</span>
                          <span className="text-sm text-gray-900">${request.actualCost}</span>
                        </div>
                      )}
                      {request.requiresBudgetApproval && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Budget Approval:</span>
                          <span className={`text-sm ${request.budgetApproved ? 'text-green-600' : 'text-yellow-600'}`}>
                            {request.budgetApproved ? 'Approved' : 'Pending'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Timeline</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Created:</span>
                      <span className="text-sm text-gray-900">{formatDate(request.createdAt)}</span>
                    </div>
                    {request.dueDate && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Due Date:</span>
                        <span className={`text-sm ${new Date(request.dueDate) < new Date() ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatDate(request.dueDate)}
                        </span>
                      </div>
                    )}
                    {request.assignedAt && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Assigned:</span>
                        <span className="text-sm text-gray-900">{formatDate(request.assignedAt)}</span>
                      </div>
                    )}
                    {request.completedAt && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Completed:</span>
                        <span className="text-sm text-gray-900">{formatDate(request.completedAt)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">People</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Requested by:</span>
                      <span className="text-sm text-gray-900">
                        {request.Employee?.firstName} {request.Employee?.lastName}
                      </span>
                    </div>
                    {request.assignedTo && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Assigned to:</span>
                        <span className="text-sm text-gray-900">
                          {request.AssignedTo?.firstName} {request.AssignedTo?.lastName}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Workflow Progress */}
                {request.currentStage && request.totalStages && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Progress</h4>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">Stage:</span>
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${(request.currentStage / request.totalStages) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-500">
                          {request.currentStage} of {request.totalStages}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Workflow Actions */}
            {canTakeAction() && ['submitted', 'under_review', 'approved'].includes(request.status) && (
              <div className="border-t border-gray-200 pt-6 mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-4">Workflow Actions</h4>
                <div className="flex flex-wrap gap-3">
                  {request.status === 'submitted' && (
                    <>
                      <button
                        onClick={() => handleWorkflowAction('approve')}
                        disabled={loading}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
                      >
                        <CheckCircleIcon className="w-4 h-4 inline mr-2" />
                        Approve
                      </button>
                      <button
                        onClick={() => handleWorkflowAction('reject')}
                        disabled={loading}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
                      >
                        <XCircleIcon className="w-4 h-4 inline mr-2" />
                        Reject
                      </button>
                    </>
                  )}
                  {request.status === 'approved' && (
                    <button
                      onClick={() => handleWorkflowAction('start')}
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                    >
                      <ClockIcon className="w-4 h-4 inline mr-2" />
                      Start Work
                    </button>
                  )}
                  {request.status === 'in_progress' && (
                    <button
                      onClick={() => handleWorkflowAction('complete')}
                      disabled={loading}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
                    >
                      <CheckCircleIcon className="w-4 h-4 inline mr-2" />
                      Mark Complete
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Attachments */}
            {request.attachments && request.attachments.length > 0 && (
              <div className="border-t border-gray-200 pt-6 mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-4">Attachments</h4>
                <div className="space-y-2">
                  {request.attachments.map((attachment, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                      <div className="flex items-center space-x-3">
                        <DocumentIcon className="w-5 h-5 text-gray-400" />
                        <span className="text-sm text-gray-900">{attachment.filename}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => window.open(`/uploads/request-attachments/${attachment.filename}`, '_blank')}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = `/uploads/request-attachments/${attachment.filename}`;
                            link.download = attachment.filename;
                            link.click();
                          }}
                          className="text-green-600 hover:text-green-800 text-sm"
                        >
                          <ArrowDownTrayIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Completion Notes */}
            {request.completionNotes && (
              <div className="border-t border-gray-200 pt-6 mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Completion Notes</h4>
                <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                  {request.completionNotes}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Close
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RequestDetails; 